import { Component, Input, OnChanges, OnDestroy, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { Campaign } from '../../models/campaign';
import { CampaignService } from '../../services/campaign.service';

/** One line of the performance table — a channel, or the campaign total. */
interface InsightRow {
    label: string;
    channelId: number;
    impressions: number;
    attentiveImpressions: number;
    grp: number;
    attentiveGrp: number;
    reach: number;
    population: number;
}

interface FrequencyRow {
    frequency: string;
    percentOfAudience: number;
    audience: number;
}

/** One slice of an audience — a gender, or an age band. */
interface BreakdownRow {
    label: string;
    reach: number;
    percentOfAudience: number;
    impressions: number;
    attentiveImpressions: number;
}

/** One booked channel's own audience insights, as its row of each comparison table. */
interface ChannelInsights {
    channelId: number;
    label: string;
    frequencyDistribution: FrequencyRow[];
    genderDistribution: BreakdownRow[];
    ageDistribution: BreakdownRow[];
}

interface Insights {
    available: boolean;
    attentiveAvailable: boolean;
    byChannel: InsightRow[];
    byCity: InsightRow[];
    /** One entry per booked channel — the panel compares them rather than showing one at a time. */
    channelAudiences: ChannelInsights[];
    total: InsightRow;
    /** Every provider call behind the figures, kept exactly as it came back. */
    rawData: ProviderCall[];
}

/** One call to the provider: which screens over which dates, what was asked, what came back. */
interface ProviderCall {
    city: string;
    surfaces: number[];
    periodFrom: string;
    periodTo: string;
    request: any;
    response: any;
}

/**
 * The Performance Insights sidebar in the booking journey: what the provider forecasts for the
 * channels and slots currently selected.
 *
 * Every section covers every channel with at least one booked slot, so the client compares screens
 * side by side. A channel earns its place by having a slot, not by being selected — there is no
 * channel picker, and adding or clearing a channel's last slot adds or removes it here.
 *
 * All of it comes out of a single provider call. The breakdowns used to be fetched one channel at a
 * time because they were believed to cost a call each; in fact the response that carries the
 * campaign totals carries every screen's own figures too, so the comparison is free.
 */
@Component({
    selector: 'app-performance-insights',
    templateUrl: './performance-insights.component.html',
    styleUrls: ['./performance-insights.component.scss']
})
export class PerformanceInsightsComponent implements OnChanges, OnDestroy {

    @Input() campaign: Campaign;

    /**
     * Whether the provider is this campaign's metrics source. The parent knows because its own
     * metrics call comes back carrying an external-data array — empty when nothing is booked yet,
     * but present, which is what separates "provider, nothing selected" from "not this provider".
     */
    @Input() enabled = false;

    /** The wizard step this instance is rendered on. Decides whether the panel opens by itself. */
    @Input() step: number;

    /** The step the panel is expanded on without being asked. */
    private static readonly DEFAULT_OPEN_STEP = 3;

    /**
     * The first step the panel appears on at all.
     *
     * Not step 2. Channels are chosen there but slots are not, so there is nothing to forecast yet —
     * the panel could only show its empty state, which would read as something being broken on a
     * step where nothing is expected of it.
     */
    private static readonly FIRST_VISIBLE_STEP = 3;

    isOpen = false;

    /** So arriving at the step opens the panel once, and does not re-open it after a close. */
    private hasAppliedDefaultOpen = false;

    insights: Insights = null;
    isLoading = false;

    /** Which button last copied, for the moment it spends confirming it worked. */
    copied: 'request' | 'response' | null = null;
    hasFailed = false;

    /**
     * The provider answered but has no forecast for what is selected (a screen or dates it doesn't
     * cover). Slots are booked, so the "select channels and slots" prompt would be wrong here.
     */
    noForecast = false;

    // The selections the latest request was for, answered or still in flight. Compared on every
    // change so an edit refreshes the figures, and so an unrelated change costs no provider call.
    // It is the requested one rather than the loaded one: undoing an edit while its figures are
    // still loading would otherwise match the old figures and never ask again (T41853).
    private requestedSignature: string = null;

    // The request in flight. A newer edit cancels it, so the latest selection always wins.
    private request: Subscription = null;

    /** Set on <body> while the panel is open, so the app layout can give up a column for it. */
    private static readonly OPEN_CLASS = 'performance-insights-open';

    /**
     * Set on <body> whenever the rail is present at all. The collapsed rail is only 46px, but the
     * page's own padding does not clear it, so it needs its own column too — without this the
     * content sat flush against it.
     */
    private static readonly RAIL_CLASS = 'performance-insights-rail';

    constructor(private campaignService: CampaignService, private renderer: Renderer2) { }

    /**
     * Whether the panel belongs on this step at all: the provider has to be the campaign's metrics
     * source, and the step has to be one where a forecast exists to look at.
     */
    get visible(): boolean {
        return this.enabled && this.step >= PerformanceInsightsComponent.FIRST_VISIBLE_STEP;
    }

    ngOnChanges() {
        // `enabled` flips once the metrics call reports which source answered, so the rail's column
        // is claimed and released here rather than once at startup.
        this.setClass(PerformanceInsightsComponent.RAIL_CLASS, this.visible);
        if (!this.visible) {
            // Nothing reserved and nothing open on a step the panel does not belong on.
            this.setClass(PerformanceInsightsComponent.OPEN_CLASS, false);
            return;
        }

        // Expanded on arrival at the scheduling step, where the figures are the point of the page;
        // collapsed on the steps after it, where they are reference. Applied once, so closing the
        // panel and editing a slot does not force it back open.
        if (!this.hasAppliedDefaultOpen && this.step === PerformanceInsightsComponent.DEFAULT_OPEN_STEP) {
            this.hasAppliedDefaultOpen = true;
            this.open();
            return;
        }

        // Only while open: a closed panel must not spend a provider call on an edit nobody asked to
        // see. Reopening it re-checks the signature and fetches then.
        if (this.isOpen && this.signatureOf(this.campaign) !== this.requestedSignature) {
            this.load();
        }
    }

    open() {
        this.isOpen = true;
        this.setClass(PerformanceInsightsComponent.OPEN_CLASS, true);
        if (this.signatureOf(this.campaign) !== this.requestedSignature) {
            this.load();
        }
    }

    close() {
        this.isOpen = false;
        this.setClass(PerformanceInsightsComponent.OPEN_CLASS, false);
    }

    /** Whether there is a provider payload behind the figures on screen. */
    get hasRawData(): boolean {
        return !!this.insights && (this.insights.rawData || []).length > 0;
    }

    /**
     * What TMC asked the provider, and what the provider answered, each on the clipboard on its
     * own, for sending back when a figure is questioned.
     *
     * Two buttons rather than one blob, because the two settle different arguments. The response
     * answers "are your figures right"; the request answers "did TMC ask the right question" — the
     * spot length, play rate and booked hours decide every number that comes back, and they appear
     * nowhere in the response.
     *
     * Copied rather than downloaded: the panel sits inside the booking journey, and a file save
     * would take the client out of it. One entry per call, each tagged with the screens and period
     * it covered, because a campaign is rarely a single call and an untagged bundle cannot be
     * matched back to anything.
     */
    copyRawData(side: 'request' | 'response') {
        if (!this.hasRawData) {
            return;
        }
        const payload = JSON.stringify({
            campaign: this.campaign ? this.campaign.title : null,
            copiedAt: new Date().toISOString(),
            calls: this.insights.rawData.map(call => ({
                city: call.city,
                surfaces: call.surfaces,
                periodFrom: call.periodFrom,
                periodTo: call.periodTo,
                [side]: call[side]
            }))
        }, null, 2);

        const done = () => {
            this.copied = side;
            // Long enough to read, short enough that the button is ready again before a second
            // copy is wanted.
            setTimeout(() => this.copied = null, 2500);
        };

        // Rejected in older browsers and wherever the page is not a secure context, so there is a
        // fallback rather than a button that silently does nothing.
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(payload).then(done, () => this.copyFallback(payload, done));
            return;
        }
        this.copyFallback(payload, done);
    }

    private copyFallback(payload: string, done: () => void) {
        const area = document.createElement('textarea');
        area.value = payload;
        // Off-screen rather than hidden: a display:none element cannot be selected, and the copy
        // would quietly produce an empty clipboard.
        area.style.position = 'fixed';
        area.style.left = '-9999px';
        document.body.appendChild(area);
        area.select();
        try {
            if (document.execCommand('copy')) {
                done();
            }
        } catch (unsupported) {
            // Nothing more to try. The button simply does not confirm, which is the honest signal.
        }
        document.body.removeChild(area);
    }

    /**
     * Stepping through the journey destroys this component, so the class has to come off with it —
     * otherwise the page keeps a column reserved for a panel that is no longer there.
     */
    ngOnDestroy() {
        if (this.request) {
            this.request.unsubscribe();
        }
        this.setClass(PerformanceInsightsComponent.OPEN_CLASS, false);
        this.setClass(PerformanceInsightsComponent.RAIL_CLASS, false);
    }

    private setClass(name: string, on: boolean) {
        if (on) {
            this.renderer.addClass(document.body, name);
        } else {
            this.renderer.removeClass(document.body, name);
        }
    }

    /** Every channel with at least one booked slot — the rows of the performance table. */
    get channels(): InsightRow[] {
        return this.insights ? this.insights.byChannel : [];
    }

    get cities(): InsightRow[] {
        return this.insights ? this.insights.byCity : [];
    }

    get attentiveAvailable(): boolean {
        return !!this.insights && this.insights.attentiveAvailable;
    }

    /**
     * One row per booked channel in the comparison tables, in the performance table's order. The
     * provider lists the breakdowns in an order of its own, which put the same channels in a
     * different order from one table to the next.
     */
    get audienceRows(): ChannelInsights[] {
        if (!this.insights) {
            return [];
        }
        const order = this.channels.map(row => row.channelId);
        const rank = (channel: ChannelInsights) => {
            const index = order.indexOf(channel.channelId);
            return index === -1 ? order.length : index;
        };
        return (this.insights.channelAudiences || []).slice().sort((a, b) => rank(a) - rank(b));
    }

    /**
     * The frequency steps to draw as columns: every step any channel reported, in order.
     *
     * A union rather than one channel's own ladder — the provider can answer 1+ and 3+ for one
     * screen and only 1+ for another, and a column dropped because one channel was short of it
     * would hide a figure another channel does have.
     */
    get frequencyColumns(): string[] {
        const all = this.columnsOf(channel => channel.frequencyDistribution,
            row => row.frequency, true);
        // The provider answers every step it models — eighteen of them, as far as 60+ — and for a
        // short campaign all but the first one or two are zero for every channel. The dead tail is
        // dropped so the table opens on the steps that carry figures.
        //
        // Only the tail: a zero between two real figures is a real answer, and cutting those would
        // move 3+ into the column where 2+ belongs.
        let last = -1;
        all.forEach((column, index) => {
            const anyone = this.audienceRows.some(channel => {
                const cell = this.cellFor(channel.frequencyDistribution, column);
                return cell && cell.audience > 0;
            });
            if (anyone) {
                last = index;
            }
        });
        return last < 0 ? [] : all.slice(0, last + 1);
    }

    get genderColumns(): string[] {
        return this.columnsOf(channel => channel.genderDistribution, row => row.label, false);
    }

    get ageColumns(): string[] {
        return this.columnsOf(channel => channel.ageDistribution, row => row.label, true);
    }

    /** The distinct labels across every channel, optionally ordered by the number they start with. */
    private columnsOf(pick: (channel: ChannelInsights) => any[],
                      label: (row: any) => string, byLeadingNumber: boolean): string[] {
        const seen: string[] = [];
        this.audienceRows.forEach(channel => (pick(channel) || []).forEach(row => {
            const value = label(row);
            if (value && seen.indexOf(value) === -1) {
                seen.push(value);
            }
        }));
        if (!byLeadingNumber) {
            return seen;
        }
        // "6 – 17" before "18 – 29", and "2+" before "10+" — a plain string sort gets both wrong.
        return seen.sort((a, b) => this.leadingNumber(a) - this.leadingNumber(b));
    }

    private leadingNumber(label: string): number {
        const match = /\d+/.exec(label || '');
        return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
    }

    /**
     * One cell: the row a channel reported for this column, or null when it reported none.
     *
     * Null rather than a zero-filled row, so the template can print a dash. A channel the provider
     * said nothing about for a band is not a channel that reached nobody in it.
     */
    cellFor(rows: any[], label: string): any {
        const row = (rows || []).find((candidate: any) =>
            (candidate.frequency || candidate.label) === label);
        return row ? row : null;
    }

    /** A channel's own total impressions, for the Total column of the gender table. */
    impressionsOf(channelId: number): number {
        const row = this.channels.find(candidate => candidate.channelId === channelId);
        return row ? row.impressions : 0;
    }

    private load() {
        if (this.request) {
            this.request.unsubscribe();
        }
        this.requestedSignature = this.signatureOf(this.campaign);
        const hasSlots = this.hasBookedSlot(this.campaign);
        this.isLoading = true;
        this.hasFailed = false;
        this.request = this.campaignService.getCampaignPerformanceInsights(this.campaign)
            .subscribe(
                (response: Insights) => {
                    this.insights = response && response.available ? response : null;
                    this.noForecast = !this.insights && hasSlots;
                    this.isLoading = false;
                },
                () => {
                    // A failed forecast leaves the panel empty rather than showing stale figures
                    // against selections that have since changed. Cleared, so reopening retries.
                    this.insights = null;
                    this.noForecast = false;
                    this.requestedSignature = null;
                    this.isLoading = false;
                    this.hasFailed = true;
                }
            );
    }

    /** Whether any channel has a slot booked on any day: a slot mask with a bit set. */
    private hasBookedSlot(campaign: any): boolean {
        return !!campaign && (campaign.products || []).some((product: any) =>
            (product.channels || []).length > 0
            && (product.dayPartings || []).some((parting: any) => /[1-9a-f]/i.test(parting.slotHex || '')));
    }

    /**
     * The selections the figures depend on: which channels are booked and on which dates and hours.
     * Anything else about the campaign changing must not cost a provider call.
     */
    private signatureOf(campaign: any): string {
        if (!campaign) {
            return '';
        }
        const products = (campaign.products || []).map((product: any) => ({
            channels: product.channels || [],
            dayPartings: (product.dayPartings || [])
                .map((parting: any) => ({ date: parting.date, slotHex: parting.slotHex }))
        }));
        return JSON.stringify({
            id: campaign._id,
            billingMethod: campaign.billingMethod,
            channels: campaign.channels || [],
            products
        });
    }
}
